import React, { useEffect, useMemo } from 'react'
import Dialog from '@mui/material/Dialog'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { Button, Checkbox, Icon, Textarea } from '../../../../components/UI'
import ErrorMessageWithExclamation from '../../../../components/UI/ErrorMessageWithExclamation'

import { selectStudentCalendarData } from '../../../../routing/fetchingfunction/dashboard/dashboard.reducer'
import {
  changeBookingStatus,
  resetChangeBookingStatusError,
} from '../../../../routing/fetchingfunction/classBannerBooking/classBannerBooking.actions'
import {
  selectErrorChangeBookingStatus,
  selectLoadingChangeBookingStatus,
} from '../../../../routing/fetchingfunction/classBannerBooking/classBannerBooking.reducer'
import { getStudentCalendar } from '../../../../routing/fetchingfunction/dashboard/dashboard.action'

import { CancelClassSchema } from '../../../../routing/utils/validators'

import { getUserName } from '../../../../helpers'

import { cancelClassReasons } from '../../../../constants'

const CancelClass = ({ data, open, onClose, onSuccess }) => {
  const dispatch = useDispatch()

  const userCalendarData = useSelector(selectStudentCalendarData)
  const isLoading = useSelector(selectLoadingChangeBookingStatus)
  const error = useSelector(selectErrorChangeBookingStatus)

  const {
    formState: { errors },
    reset,
    watch,
    setValue,
    control,
    handleSubmit,
  } = useForm({
    defaultValues: {
      reason: '',
      reason_description: '',
    },
    resolver: yupResolver(CancelClassSchema),
  })

  const reason = watch('reason')

  const timeDetector = useMemo(() => {
    const key = `${moment(data).format('YYYY-MM-DD')}:${moment(data).format('H')}`

    if (!userCalendarData[key]) {
      return null
    }

    return userCalendarData[key]
  }, [data])

  const classBanner = timeDetector?.availability_calendar_detail?.class_banner

  const handleChangeReason = (value) => {
    if (value === reason) {
      return setValue('reason', '')
    }

    setValue('reason', value)
  }

  const handleFormSubmit = (data) => {
    dispatch(
      changeBookingStatus({
        id: timeDetector.booking_hash_1,
        data: {
          ...data,
          status: 'cancelled',
        },
      })
    ).then((response) => {
      if (!response.error) {
        onSuccess(response.payload || timeDetector)

        dispatch(getStudentCalendar())
      }
    })
  }

  useEffect(() => {
    if (!open) {
      dispatch(resetChangeBookingStatusError())
      reset()
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="body"
      fullWidth
      classes={{
        paper: 'relative md:!max-w-[600px] !p-[32px] !rounded-[10px] !bg-[#FEEBDD]',
      }}
    >
      {timeDetector && (
        <div className="w-full flex flex-col items-center justify-center">
          <h2 className="text-2xl font-bold text-primary-100">
            {classBanner.class_banner_category.name}, with{' '}
            {getUserName(
              classBanner.class_banner_creator,
              classBanner.creator_profile?.display_name,
              classBanner.creator_profile?.display_last_name
            )}
          </h2>

          <h3 className="mt-[12px] text-base font-normal text-primary-100">
            Why do you want to cancel this class?
          </h3>

          {error && <ErrorMessageWithExclamation text={error} className="mr-auto !mt-[12px]" />}

          <form className="w-full" onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="w-full flex flex-col gap-y-[6px] mt-[12px]">
              {cancelClassReasons.map((cancelClassReason) => {
                const isActive = reason === cancelClassReason.value

                return (
                  <Checkbox
                    key={cancelClassReason.value}
                    label={cancelClassReason.label}
                    wrapperClassName={`h-[44px] px-[14px] rounded-[8px] border-[1px] shadow-button ${isActive ? 'bg-primary-button border-primary-button' : 'border-[#FFCA84] bg-white'}`}
                    labelClassName={`text-base font-normal ${isActive ? 'text-[#FDFDFD]' : 'text-primary-100'} transition-colors duration-200 ease`}
                    value={cancelClassReason.value}
                    checked={isActive}
                    onChange={() => handleChangeReason(cancelClassReason.value)}
                  />
                )
              })}
            </div>

            {errors.reason && <ErrorMessageWithExclamation text={errors.reason.message} />}

            <div className="flex flex-col items-start w-full mt-[36px]">
              <label htmlFor="" className="mb-[6px] text-sm font-medium text-primary-100">
                Tell us your reason
              </label>

              <Controller
                control={control}
                name="reason_description"
                render={({ field }) => (
                  <Textarea
                    rows={4}
                    className="w-full resize-none text-primary-100"
                    wrapperClassName="w-full"
                    placeholder="Enter a description..."
                    {...field}
                  />
                )}
              />

              {errors.reason_description && (
                <ErrorMessageWithExclamation text={errors.reason_description.message} />
              )}
            </div>

            <div className="p-[16px] w-full mt-[12px] rounded-[10px] bg-[#FFF5EE] flex items-center">
              <Icon name="info" color="#F79009" className="w-[24px] h-[24px] mr-[10px]" />

              <p className="flex-1 text-base font-normal text-primary-100">
                Please note that you cannot delay or cancel more than 30% of your weekly classes
                (maximum limit of 5 classes). If you are a no-show, this class will count as a
                cancelled class.
              </p>
            </div>

            <div className="w-full flex items-center gap-x-[10px] mt-[12px]">
              <button
                type="button"
                className="flex-1 h-[44px] rounded-[8px] bg-[#FDFDFD] border-primary-button border-[1px] shadow-button-second text-base text-primary-100 font-semibold transition-colors duration-200 ease hover:!border-primary-button-hover hover:!bg-primary-button-hover hover:text-white"
                onClick={onClose}
              >
                Back
              </button>

              <Button type="submit" className="flex-1" isLoading={isLoading}>
                Confirm Cancel Class
              </Button>
            </div>
          </form>
        </div>
      )}
    </Dialog>
  )
}

export default CancelClass
